import * as React from 'react'
import { theme } from '../theme'
import { ArticleUrlInput } from './ArticleUrlInput'
import { CustomPromptInput } from './CustomPromptInput'
import { ImageUploadInput } from './ImageUploadInput'
import { type AiGenerationState, type AiGenerationHandlers } from './useAiGeneration'
import { GenerationTypeSelector } from './GenerationTypeSelector'
import { DimensionOptions } from '../dimension_management/DimensionOptions'
import { GenerateButton } from './GenerateButton'
import { GenerationModeSelector, type GenerationMode } from './GenerationModeSelector'

// Constants for generation types
const generationTypes = {
    landingPage: 'landingPage',
    referenceImage: 'referenceImage',
    prompt: 'prompt',
}

type Props = AiGenerationState & AiGenerationHandlers & {
    generationMode?: GenerationMode
    onGenerationModeChange?: (mode: GenerationMode) => void
    imageCanvasRef?: React.RefObject<HTMLCanvasElement>
    hideDimensionOptions?: boolean
    registerImageRef?: (dimensionId: string, element: HTMLImageElement | null) => void
    registerContainerRef?: (dimensionId: string, element: HTMLDivElement | null) => void
}

export const AiGenerationInputs = ({
    articleUrls,
    customPrompt,
    referenceImage,
    error,
    generationType,
    selectedDimension,
    isGenerating,
    generationMode = 'structure',
    isImageSuitableForStructure,
    referenceImageDimensions,
    handleArticleUrlsChange,
    handleCustomPromptChange,
    handleReferenceImageSelect,
    handleGenerationTypeChange,
    handleDimensionChange,
    handleGenerate,
    onGenerationModeChange,
    imageCanvasRef,
    hideDimensionOptions = false,
    registerImageRef,
    registerContainerRef
}: Props) => {
    // Compute button disabled state based on generation type
    const isGenerateDisabled = React.useMemo(() => {
        if (generationType === generationTypes.landingPage) {
            return !articleUrls.trim()
        } else if (generationType === generationTypes.prompt) {
            return !customPrompt.trim()
        } else {
            return !referenceImage || !customPrompt.trim()
        }
    }, [generationType, articleUrls, referenceImage, customPrompt])

    // Handler for mode changes with fallback
    const handleModeChange = React.useCallback((mode: GenerationMode) => {
        if (onGenerationModeChange) {
            onGenerationModeChange(mode)
        }
    }, [onGenerationModeChange])

    // Generate the structure disabled reason message
    const structureDisabledReason = React.useMemo(() => {
        if (referenceImageDimensions) {
            return `Image resolution (${referenceImageDimensions.width}x${referenceImageDimensions.height}) is too small for structure-based generation. Minimum required is 1200x628.`
        }
        return 'Image resolution is too small for structure-based generation. Minimum required is 1200x628.'
    }, [referenceImageDimensions])

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.md,
                fontSize: theme.typography.sizes.tiny,
                color: theme.colors.text.secondary,
                width: '100%',
                maxWidth: '100%',
            }}
        >
            <GenerationTypeSelector
                value={generationType}
                onChange={handleGenerationTypeChange}
            />

            {!hideDimensionOptions && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing.sm,
                    width: '100%',
                }}>
                    <label style={{
                        fontSize: theme.typography.sizes.tiny,
                        fontWeight: 500,
                        color: theme.colors.text.primary,
                    }}>
                        Select Output Dimensions
                    </label>
                    <DimensionOptions
                        selectedOption={selectedDimension}
                        onSelect={handleDimensionChange}
                        imageCanvasRef={imageCanvasRef}
                        containerWidth={300}
                        showPreviews={!!imageCanvasRef}
                        registerImageRef={registerImageRef}
                        registerContainerRef={registerContainerRef}
                    />
                </div>
            )}

            {generationType === generationTypes.landingPage && (
                <ArticleUrlInput
                    value={articleUrls}
                    onChange={handleArticleUrlsChange}
                />
            )}

            {generationType === generationTypes.referenceImage && (
                <>
                    <ImageUploadInput
                        onImageSelect={handleReferenceImageSelect}
                        selectedImage={referenceImage}
                        error={error}
                    />
                    <GenerationModeSelector
                        value={generationMode}
                        onChange={handleModeChange}
                        isStructureDisabled={!isImageSuitableForStructure && !!referenceImage}
                        structureDisabledReason={structureDisabledReason}
                    />
                    <CustomPromptInput
                        value={customPrompt}
                        onChange={handleCustomPromptChange}
                        label="Describe your picture"
                    />
                </>
            )}

            {generationType === generationTypes.prompt && (
                <CustomPromptInput
                    value={customPrompt}
                    onChange={handleCustomPromptChange}
                    label="Describe your picture"
                />
            )}

            <div style={{
                marginTop: 'auto',
                paddingTop: theme.spacing.md,
                borderTop: `1px solid ${theme.colors.border}`,
            }}>
                <GenerateButton
                    onClick={handleGenerate}
                    disabled={isGenerateDisabled}
                    isLoading={isGenerating}
                />
            </div>
        </div>
    )
}
